import React, { useEffect, useRef, useState } from 'react';
import { Box, Center, Heading, Image, Text, useColorModeValue, useToast, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../../app/store';
import noWifiConnectionImg from '../../../assets/images/globe-lines-arrows-slash.svg';
import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../../common/constants';
import { useTariffs } from '../../../features/tariff/tariffApi';
import { OutlineGradientIcon } from '../../../styles/custom-icons';
import { trackChargerAddedSuccessfully } from '../../../utils/common/analytics';
import { waitForReconnectGate } from '../../../utils/common/wifi-reconnect-utils';
import { trackDynatraceChargerAddedSuccessfully } from '../../../utils/native/dynatrace-events';
import { selectSite } from '../../site/siteSlice';
import { selectDeviceSetupState, syncDetectedEvseModel } from '../device-setup/deviceSetupSlice';
import { selectWifiConfigState } from './wifiConfigSlice';

/* istanbul ignore next -- @preserve */
export const NoWifiConnection = () => {
  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const { t } = useTranslation();
  const retryInFlightRef = useRef(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { isEditing } = useSelector(selectWifiConfigState);
  const { qrCodeValues } = useSelector(selectDeviceSetupState);
  const site = useSelector(selectSite);
  const { isOnboarding } = useSelector(selectSite);
  const { tariffs } = useTariffs();
  const redIconColor = useColorModeValue('#FF3E24', '#FF5B44');
  const toast = useToast();

  const onboardingTariffPath = `/sites/${site.site_id}/account/tariff/new?direction=forward&backURL=/sites/${site.site_id}/home/device-status?direction=back`;

  useEffect(() => {
    if (!qrCodeValues) {
      navigate('../device-scan');
    }
  }, [navigate, qrCodeValues]);

  function handleSuccessfulSetupNavigation() {
    dispatch(syncDetectedEvseModel());

    if (isEditing) {
      navigate('../device-wifi-configuration', { replace: true });
      return;
    }

    trackChargerAddedSuccessfully();
    trackDynatraceChargerAddedSuccessfully();

    if (isOnboarding && tariffs.length === 0) {
      navigate(onboardingTariffPath, { replace: true });
    } else {
      navigate('../device-status', { replace: true });
    }
  }

  async function handleReconnectRetry() {
    if (retryInFlightRef.current) {
      return;
    }

    retryInFlightRef.current = true;
    setIsRetrying(true);

    try {
      const canContinue = await waitForReconnectGate(qrCodeValues?.wifi?.ssid);
      if (!canContinue) {
        toast({
          title: t('EVSE.retry unsuccessful title'),
          description: t('EVSE.retry unsuccessful description'),
          status: 'warning',
          isClosable: true,
        });
        return;
      }

      handleSuccessfulSetupNavigation();
    } finally {
      retryInFlightRef.current = false;
      setIsRetrying(false);
    }
  }

  return (
    // Pull-to-refresh reloads the whole app, wiping qrCodeValues while still connected to the EVSE
    // AP with no internet - stranding the user on the generic InternetConnectionRecoveryPage.
    <SlidingAnimationPageBase px={4} title={t('Common.device setup')} disablePullToRefresh>
      <Box display="flex" flexDirection="column" h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}>
        <Center pt={4} pb={2}>
          <Box position="relative" w="48vw" h="48vw">
            <OutlineGradientIcon w="100%" h="100%" color={redIconColor} data-testid="outline-icon" />
            <Image
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              w="87px"
              h="74px"
              data-testid="no-wifi-connection-image"
              src={noWifiConnectionImg}
              alt={t('EVSE.no wifi connection title')}
            />
          </Box>
        </Center>

        <VStack align="stretch" spacing={2} mb={2}>
          <Heading fontSize={24} fontWeight={600} textAlign="center" data-testid="no-wifi-connection-title">
            {t('EVSE.no wifi connection title')}
          </Heading>
        </VStack>

        <VStack align="stretch" spacing={2}>
          <Text textAlign="center">{t('EVSE.no wifi connection description primary')}</Text>
          <Text textAlign="center">{t('EVSE.no wifi connection description secondary')}</Text>
        </VStack>

        <Center mt="auto" mb={5} flexDir={'column'}>
          <ArcButton
            data-testid="no-wifi-connection-retry-button"
            onClick={handleReconnectRetry}
            isDisabled={isRetrying}
            isLoading={isRetrying}
            w="100%"
            py={6}
            mt="auto"
            arcColor="#3DCD57"
          >
            {t('EVSE.online now proceed')}
          </ArcButton>
        </Center>
      </Box>
    </SlidingAnimationPageBase>
  );
};
